/**
 * Viori Beauty Expert — storefront client bundle v2 (inline-card paradigm).
 *
 * Companion to the Worker at viori-beauty-expert-staging.<account>.workers.dev
 * (see beauty-expert-build-spec-v1.4.docx). This bundle:
 *
 *   - mounts an INLINE CARD on /collections/all (not a floating pill)
 *   - sits where "Take the Quiz" used to live, replacing it like-for-like
 *   - has two states: dormant (input + opener chips) and expanded (greeting + transcript)
 *   - hides the V6 Filter Bar via display:none while expanded; restores on X-out
 *   - never auto-opens; tap to engage
 *   - transcript is in-memory only; every page load starts fresh
 *   - issues bx_sid cookie client-side; computes A/B group; control group sees nothing
 *   - warms up worker on hover (desktop) or first chip tap / first keystroke (mobile)
 *   - streams replies via client-side typewriter on top of JSON response
 *   - calls window.VioriFilter.applyFilters({source:'bot', ...}) to write filters
 *   - never surfaces in-card products; V6 Product Grid below is the only surface
 *
 * The Worker URL is read from the mount element's data attribute:
 *   <div data-beauty-expert data-worker-url="https://...">
 *
 * No bundler. ES5 syntax (matches filter-controller.js convention).
 */

(function () {
  'use strict';

  /* ── Constants ─────────────────────────────────────────────── */

  var BUNDLE_VERSION = 'be-client-2.2.0';
  var COOKIE_NAME = 'bx_sid';
  var COOKIE_DAYS = 30;
  var HISTORY_LIMIT = 12;
  var TURN_TIMEOUT_MS = 15000;
  var TURN_INPUT_MAXLEN = 2000;
  var TYPEWRITER_MS_PER_CHAR = 18;
  var STORAGE_KEY = 'be_transcript_v2';

  // A/B test split: hash(bx_sid) mod 100. < threshold = test (bot rendered); >= threshold = control.
  // Set to 100 to disable A/B testing — every visitor falls into the test group (hash mod 100 ∈ [0, 99]).
  var AB_TEST_THRESHOLD = 100;

  // Bundle is restricted to /collections/all. Mount returns early on any other path.
  var ALLOWED_PATH_PATTERN = /^\/collections\/all(\/|\?|$|#)/;

  // V6 Filter Bar selectors, tried in order. The bundle hides the first match.
  // Override via data-filter-bar-selector on the mount element if needed.
  var DEFAULT_FILTER_BAR_SELECTORS = [
    '[data-cust-filter-bar]',
    '.cust-filter-bar',
    '[data-section-type="cust-filter-bar"]',
    '[id^="shopify-section-"][id$="cust-filter-bar"]'
  ];

  // Five canonical opener chips, used in the "inline_full" dormant mode.
  // Q1 (shopping_mission) is tone-only per Phase A lock — does not write a filter.
  var OPENER_CHIPS = [
    "I'm shopping for myself",
    "My hair is dry and frizzy",
    "I want more volume and shine",
    "I have an oily scalp",
    "I have curly hair"
  ];

  // Opening suggestion pills shown when the chat first expands. The merchant
  // picks 1–7 via the section schema (data-initial-pill-count); we slice this
  // array to that count. Each pill is a short visitor-voiced question that
  // maps cleanly to a canonical taxonomy filter the worker knows how to write.
  var INITIAL_PILLS = [
    "I have dry, frizzy hair",
    "I want more volume",
    "I have an oily scalp",
    "I want longer hair",
    "I want shinier hair",
    "I have curly hair",
    "I want fragrance-free options"
  ];

  // Default count if the schema attribute is missing or invalid.
  var DEFAULT_INITIAL_PILL_COUNT = 5;
  var MAX_INITIAL_PILL_COUNT = 7;

  var GREETING_TITLE = "Hi, I'm your Beauty Expert.";
  var GREETING_SUB = "Tell me what you're looking for and I'll narrow the catalog for you.";
  var INPUT_PLACEHOLDER = "How can I help you find the right products?";
  var SEND_LABEL = "Send";
  var MIC_LABEL_IDLE = "Dictate message";
  var MIC_LABEL_LISTENING = "Stop dictation";
  var SR_CTOR = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  var SPEECH_SUPPORTED = !!SR_CTOR;
  var COLLAPSE_LABEL = "Close chat";
  var RESTART_LABEL = "Restart conversation";
  var YOU_LABEL = "You";
  var BOT_LABEL_DEFAULT = "Beauty Expert";
  var CTA_LABEL_DEFAULT = "Talk to your Beauty Expert";

  /* ── Utility helpers ───────────────────────────────────────── */

  function uuidv4() {
    var bytes;
    if (window.crypto && window.crypto.getRandomValues) {
      bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
    } else {
      bytes = new Array(16);
      for (var i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = [];
    for (var j = 0; j < 16; j++) {
      hex.push((bytes[j] + 0x100).toString(16).slice(1));
    }
    return (
      hex.slice(0, 4).join('') + '-' +
      hex.slice(4, 6).join('') + '-' +
      hex.slice(6, 8).join('') + '-' +
      hex.slice(8, 10).join('') + '-' +
      hex.slice(10, 16).join('')
    );
  }

  function readCookie(name) {
    var parts = document.cookie.split(';');
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim();
      if (p.indexOf(name + '=') === 0) {
        return decodeURIComponent(p.substring(name.length + 1));
      }
    }
    return null;
  }

  function writeCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    var attrs =
      'expires=' + d.toUTCString() +
      '; path=/; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
    document.cookie = name + '=' + encodeURIComponent(value) + '; ' + attrs;
  }

  function getOrCreateSessionId() {
    var existing = readCookie(COOKIE_NAME);
    if (existing && existing.indexOf('bx_sid_') === 0) return existing;
    var fresh = 'bx_sid_' + uuidv4().replace(/-/g, '');
    writeCookie(COOKIE_NAME, fresh, COOKIE_DAYS);
    return fresh;
  }

  // Deterministic hash on the cookie value, mod 100.
  // Consistent across page loads + return visits within the cookie lifespan.
  function abGroupBucket(sessionId) {
    var hash = 5381;
    for (var i = 0; i < sessionId.length; i++) {
      hash = ((hash << 5) + hash + sessionId.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % 100;
  }

  function getViewport() {
    var w = window.innerWidth || document.documentElement.clientWidth;
    if (w < 750) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  }

  function getCollectionHandle() {
    var match = location.pathname.match(/^\/collections\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function getCurrentFilters() {
    var params = new URLSearchParams(window.location.search);
    var out = {};
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) {
        if (!out[key]) out[key] = [];
        out[key].push(value);
      }
    });
    return out;
  }

  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function readSessionTranscript() {
    // Persistence intentionally disabled per product decision 2026-05-02.
    // Every page load starts the bot with a clean transcript. Clear any
    // leftover key written by older bundle versions so old chats don't
    // resurrect when this version first loads.
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (_e) {}
    return [];
  }

  function writeSessionTranscript(_history) {
    // Persistence intentionally disabled — see readSessionTranscript().
  }

  /* ── BeautyExpertCard ──────────────────────────────────────── */

  function BeautyExpertCard(mountEl) {
    this.mountEl = mountEl;
    this.workerUrl = mountEl.getAttribute('data-worker-url') || '';
    this.filterBarSelector = mountEl.getAttribute('data-filter-bar-selector') || '';
    // Schema-driven settings (added in v2.0.3)
    var dm = mountEl.getAttribute('data-dormant-mode') || 'cta_button';
    this.dormantMode = (dm === 'inline_full') ? 'inline_full' : 'cta_button';
    this.ctaLabel = mountEl.getAttribute('data-cta-label') || CTA_LABEL_DEFAULT;
    this.botName = mountEl.getAttribute('data-bot-name') || BOT_LABEL_DEFAULT;
    var avatarUrl = mountEl.getAttribute('data-bot-avatar-url') || '';
    this.botAvatarUrl = (avatarUrl && avatarUrl.length > 0) ? avatarUrl : '';
    // Greeting copy — schema-driven with fallback to module defaults so the
    // section still renders sensibly if the merchant blanks both fields.
    var titleAttr = mountEl.getAttribute('data-chat-title');
    var subAttr = mountEl.getAttribute('data-chat-subtitle');
    this.greetingTitle = (titleAttr && titleAttr.length > 0) ? titleAttr : GREETING_TITLE;
    this.greetingSub = (subAttr && subAttr.length > 0) ? subAttr : GREETING_SUB;
    // Input placeholder — schema-driven with fallback so blanking the field
    // in the editor still gives the visitor a sensible cue.
    var phAttr = mountEl.getAttribute('data-input-placeholder');
    this.inputPlaceholder = (phAttr && phAttr.length > 0) ? phAttr : INPUT_PLACEHOLDER;
    // Number of initial suggestion pills (merchant-controlled, 1..7).
    var pillCountRaw = parseInt(mountEl.getAttribute('data-initial-pill-count') || '', 10);
    if (isNaN(pillCountRaw) || pillCountRaw < 1) pillCountRaw = DEFAULT_INITIAL_PILL_COUNT;
    if (pillCountRaw > MAX_INITIAL_PILL_COUNT) pillCountRaw = MAX_INITIAL_PILL_COUNT;
    this.initialPillCount = pillCountRaw;
    this.locale = (document.documentElement.lang || 'en-US');
    this.sessionId = getOrCreateSessionId();
    this.history = readSessionTranscript();
    this.firstExpansion = true;
    // Bryce's rule (2026-05-03): every customer input must produce a
    // product output below. Once a turn has completed, the rail is
    // committed to showing products and must not collapse to empty
    // mid-fetch (e.g., while the V6 grid is being wiped + repopulated by
    // the universal Best Sellers fallback). _hasCompletedTurn flips on
    // the first successful turn and stays true for the life of the card.
    this._hasCompletedTurn = false;
    this.cardEl = null;
    this.transcriptEl = null;
    this.chipsEl = null;
    this.inputEl = null;
    this.sendBtnEl = null;
    this.closeBtnEl = null;
    this.refreshBtnEl = null;
    this.railEl = null;
    this.railTrackEl = null;
    // Most recent set of bot-recommended products (rank-ordered handles).
    // Populated from `recommended_products` in each worker reply. The carousel
    // leads with these; secondary fillers come from the V6 grid mirror.
    // Cleared on _collapse and _restart.
    this.lastRecommendations = [];
    // Generation counter for async card fetches. Each _renderProducts call
    // bumps this; in-flight SRA fetches check the counter on resolve and
    // discard their result if a newer render has already started. Prevents
    // a stale fetch from clobbering a fresh rail.
    this._renderGen = 0;
    // be-client-2.2.0: suppress event-driven re-renders during the turn
    // response window. Prevents vioriFiltersChanged / v6:cards-rendered from
    // thrashing the rail while the grid is mid-SRA-fetch for the new filter set.
    this._turnRenderActive = false;
    this._turnRenderTimeoutMs = 3000;
    this._turnEpoch = 0;
    // In-memory cache of fetched V6 card HTML keyed by handle. Saves a fetch
    // when the same handle is recommended in consecutive turns.
    this._cardHtmlCache = {};
    this.expanded = false;
    this.warmedUp = false;
    this.isSending = false;
    this.savedFilterBarDisplay = null;
    this.filterBarEl = null;

    this._build();
    if (this.history.length > 0) {
      // Visitor refreshed mid-conversation. Auto-expand and re-render transcript.
      this._expand({ skipFocus: true });
      this._renderHistory();
    }
  }

  BeautyExpertCard.prototype._build = function () {
    var self = this;
    var card = document.createElement('div');
    card.className = 'be-card';
    var initialState = this.dormantMode === 'cta_button' ? 'cta' : 'dormant';
    card.setAttribute('data-state', initialState);

    // The card holds three regions: a CTA pill (visible when state="cta"),
    // and the head/transcript/form (visible in dormant + expanded). State
    // transitions toggle visibility + drive the CSS morph.
    card.innerHTML =
      '<button type="button" class="be-cta" aria-label="' + escapeHtml(this.ctaLabel) + '">' +
        '<span class="be-cta__avatar" aria-hidden="true">' + self._renderBotAvatar() + '</span>' +
        '<span class="be-cta__label">' + escapeHtml(this.ctaLabel) + '</span>' +
      '</button>' +
      '<div class="be-card__head">' +
        '<div class="be-card__avatar" aria-hidden="true">' + self._renderBotAvatar() + '</div>' +
        '<div class="be-card__heading">' +
          '<div class="be-card__title">' + escapeHtml(this.greetingTitle) + '</div>' +
          '<div class="be-card__sub">' + escapeHtml(this.greetingSub) + '</div>' +
        '</div>' +
        '<div class="be-card__actions">' +
          '<button type="button" class="be-card__close" aria-label="' + escapeHtml(COLLAPSE_LABEL) + '">' +
            '<span aria-hidden="true">&times;</span>' +
          '</button>' +
          '<button type="button" class="be-card__refresh" aria-label="' + escapeHtml(RESTART_LABEL) + '" title="' + escapeHtml(RESTART_LABEL) + '">' +
            self._renderRefreshSvg() +
          '</button>' +
        '</div>' +
      '</div>' +
      '<div class="be-card__transcript" role="log" aria-live="polite" aria-relevant="additions"></div>' +
      '<form class="be-card__form" autocomplete="off">' +
        '<div class="be-card__input-row">' +
          '<span class="be-card__input-icon" aria-hidden="true">' + self._renderSparkleSvg() + '</span>' +
          '<input type="text" class="be-card__input" maxlength="' + TURN_INPUT_MAXLEN + '" ' +
            'placeholder="' + escapeHtml(self.inputPlaceholder) + '" aria-label="Your message" />' +
          (SPEECH_SUPPORTED
            ? '<button type="button" class="be-card__mic" data-be-mic ' +
                'aria-label="' + escapeHtml(MIC_LABEL_IDLE) + '" ' +
                'aria-pressed="false">' +
                self._renderMicSvg() +
              '</button>'
            : '') +
          '<button type="submit" class="be-card__send" aria-label="' + escapeHtml(SEND_LABEL) + '">' +
            self._renderSendSvg() +
          '</button>' +
        '</div>' +
        '<div class="be-card__chips" role="group" aria-label="Suggestions"></div>' +
        '<div class="be-card__rail" data-be-rail hidden>' +
          '<div class="be-card__rail-track" data-be-rail-track></div>' +
        '</div>' +
      '</form>';

    this.cardEl = card;
    this.ctaEl = card.querySelector('.be-cta');
    this.transcriptEl = card.querySelector('.be-card__transcript');
    this.chipsEl = card.querySelector('.be-card__chips');
    this.inputEl = card.querySelector('.be-card__input');
    this.sendBtnEl = card.querySelector('.be-card__send');
    this.micBtnEl = card.querySelector('[data-be-mic]');
    this.recognition = null;
    this.isListening = false;
    this.dictationBaseValue = '';
    this.closeBtnEl = card.querySelector('.be-card__close');
    this.refreshBtnEl = card.querySelector('.be-card__refresh');
    this.railEl = card.querySelector('.be-card__rail');
    this.railTrackEl = card.querySelector('.be-card__rail-track');

    // Initial pills depend on dormant mode:
    //   inline_full → 5 opener chips visible from the start
    //   cta_button  → no chips rendered yet; INITIAL_PILLS render after the
    //                 CTA is tapped (in _expand)
    if (this.dormantMode === 'inline_full') {
      this._renderChips(OPENER_CHIPS);
    }

    // Wire events
    card.querySelector('.be-card__form').addEventListener('submit', function (e) {
      e.preventDefault();
      self._submit();
    });
    this.closeBtnEl.addEventListener('click', function () {
      self._collapse();
    });
    this.refreshBtnEl.addEventListener('click', function (e) {
      // Stop the head-row click from re-expanding after we restart.
      e.stopPropagation();
      self._restart();
    });

    // Speech recognition — mic button
    this._initSpeechRecognition();

    // CTA button — taps expand the card. Warm up the worker on first hover/tap.
    this.ctaEl.addEventListener('mouseenter', function () { self._warmUp(); });
    this.ctaEl.addEventListener('click', function () {
      self._warmUp();
      self._expand();
      // Slight delay before focus so the morph reads visually first.
      setTimeout(function () { try { self.inputEl.focus({ preventScroll: false }); } catch (_e) { self.inputEl.focus(); } }, 220);
    });

    // Expand triggers (used in inline_full mode and after CTA expansion)
    this.inputEl.addEventListener('focus', function () {
      self._warmUp();
      self._expand();
    });
    this.inputEl.addEventListener('input', function () {
      self._warmUp();
    });

    // Hover warm-up (desktop)
    card.addEventListener('mouseenter', function () {
      self._warmUp();
    });

    // Tap-to-expand on the head row (only relevant in inline_full mode)
    card.querySelector('.be-card__head').addEventListener('click', function (e) {
      if (e.target === self.closeBtnEl || self.closeBtnEl.contains(e.target)) return;
      if (e.target === self.refreshBtnEl || self.refreshBtnEl.contains(e.target)) return;
      if (!self.expanded) {
        self._expand();
        self.inputEl.focus();
      }
    });

    // Mirror the V6 product grid into the chat-panel rail.
    // Two hooks: filter-controller fires `vioriFiltersChanged` when state
    // changes, and the grid fires `v6:cards-rendered` after it finishes
    // re-rendering. We listen to both — `v6:cards-rendered` is the truth-
    // moment (DOM is settled), and the filter event covers cases where the
    // grid is empty / not present.
    window.addEventListener('vioriFiltersChanged', function () {
      if (self._turnRenderActive) return;
      if (self.expanded) self._renderProducts();
    });
    document.addEventListener('v6:cards-rendered', function () {
      if (self._turnRenderActive) return;
      if (self.expanded) self._renderProducts();
    }, true);

    this.mountEl.appendChild(card);
  };

  BeautyExpertCard.prototype._renderAvatarSvg = function () {
    // Viori 'V' mark in brand pink — the default bot avatar.
    return '' +
      '<svg viewBox="0 0 32 32" width="32" height="32" xmlns="http://www.w3.org/2000/svg">' +
        '<circle cx="16" cy="16" r="16" fill="#d90073"></circle>' +
        '<path d="M9 9.5 L16 22.5 L23 9.5" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"></path>' +
      '</svg>';
  };

  // Bot avatar with image override. If the section schema set a bot_avatar_image,
  // emit an <img> tag; otherwise fall back to the Viori V SVG.
  BeautyExpertCard.prototype._renderBotAvatar = function () {
    if (this.botAvatarUrl) {
      return '<img src="' + escapeHtml(this.botAvatarUrl) + '" alt="" loading="lazy" />';
    }
    return this._renderAvatarSvg();
  };

  BeautyExpertCard.prototype._renderUserAvatarSvg = function () {
    return '' +
      '<svg viewBox="0 0 32 32" width="28" height="28" xmlns="http://www.w3.org/2000/svg">' +
        '<circle cx="16" cy="16" r="16" fill="#f3e6ed"></circle>' +
        '<circle cx="16" cy="13" r="4.5" fill="#9e6582"></circle>' +
        '<path d="M6 26 C 8 21 12 19 16 19 C 20 19 24 21 26 26 Z" fill="#9e6582"></path>' +
      '</svg>';
  };

  BeautyExpertCard.prototype._renderSendSvg = function () {
    return '' +
      '<svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M2 21 L23 12 L2 3 L2 10 L17 12 L2 14 Z" fill="currentColor"></path>' +
      '</svg>';
  };

  // Refresh / restart icon for the chat panel head — pink line circle with
  // an arrowhead at the end, drawn in currentColor so the button's color
  // controls the stroke. Sized to fit the 28x28 button comfortably.
  BeautyExpertCard.prototype._renderRefreshSvg = function () {
    return '' +
      '<svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M20 11A8 8 0 1 0 12 20"></path>' +
        '<polyline points="20 5 20 11 14 11"></polyline>' +
      '</svg>';
  };

  // Gray 4-point sparkle for the input row — signals "AI assistant" input.
  // Two sparkles (one big, one small) for the classic ✨ shape, drawn in
  // currentColor so the wrapper span controls the gray.
  BeautyExpertCard.prototype._renderSparkleSvg = function () {
    return '' +
      '<svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M13 2 L14.6 8.4 L21 10 L14.6 11.6 L13 18 L11.4 11.6 L5 10 L11.4 8.4 Z" fill="currentColor"></path>' +
        '<path d="M19 14 L19.7 16.3 L22 17 L19.7 17.7 L19 20 L18.3 17.7 L16 17 L18.3 16.3 Z" fill="currentColor"></path>' +
      '</svg>';
  };

  BeautyExpertCard.prototype._renderMicSvg = function () {
    return '' +
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true">' +
        '<rect x="9" y="2" width="6" height="12" rx="3"></rect>' +
        '<path d="M5 11a7 7 0 0 0 14 0"></path>' +
        '<line x1="12" y1="18" x2="12" y2="22"></line>' +
        '<line x1="8" y1="22" x2="16" y2="22"></line>' +
      '</svg>';
  };

  BeautyExpertCard.prototype._initSpeechRecognition = function () {
    var self = this;
    if (!SPEECH_SUPPORTED || !this.micBtnEl) return;

    this.micBtnEl.addEventListener('click', function (e) {
      e.preventDefault();
      if (self.isListening) {
        if (self.recognition) {
          try { self.recognition.stop(); } catch (_) {}
        }
      } else {
        self._startDictation();
      }
    });
  };

  BeautyExpertCard.prototype._startDictation = function () {
    var self = this;
    try {
      var rec = new SR_CTOR();
      rec.continuous = false;
      rec.interimResults = true;
      rec.lang = 'en-US';
      rec.maxAlternatives = 1;

      self.dictationBaseValue = self.inputEl
        ? (self.inputEl.value || '')
        : '';

      rec.onstart = function () {
        self.isListening = true;
        if (self.micBtnEl) {
          self.micBtnEl.classList.add('is-listening');
          self.micBtnEl.setAttribute('aria-pressed', 'true');
          self.micBtnEl.setAttribute('aria-label', MIC_LABEL_LISTENING);
        }
      };

      rec.onresult = function (event) {
        var transcript = '';
        for (var i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        if (!self.inputEl) return;
        var prefix = self.dictationBaseValue;
        var sep = (prefix && !/\s$/.test(prefix)) ? ' ' : '';
        self.inputEl.value = prefix + sep + transcript;
      };

      rec.onerror = function () { /* swallow — onend cleans up */ };

      rec.onend = function () {
        self.isListening = false;
        self.recognition = null;
        if (self.micBtnEl) {
          self.micBtnEl.classList.remove('is-listening');
          self.micBtnEl.setAttribute('aria-pressed', 'false');
          self.micBtnEl.setAttribute('aria-label', MIC_LABEL_IDLE);
        }
        if (self.inputEl) self.inputEl.focus();
      };

      self.recognition = rec;
      rec.start();
    } catch (err) {
      self.isListening = false;
      if (self.micBtnEl) {
        self.micBtnEl.classList.remove('is-listening');
      }
    }
  };

  /* ── State transitions ─────────────────────────────────────── */

  BeautyExpertCard.prototype._expand = function (opts) {
    if (this.expanded) return;
    this.expanded = true;
    this.cardEl.setAttribute('data-state', 'expanded');
    this._hideFilterBar();

    // First expansion in CTA mode: render the 3 initial suggestion pills below
    // the input. If history exists from a prior turn (worker-driven followups),
    // _renderChips replaces these on each new bot turn.
    if (this.firstExpansion && this.dormantMode === 'cta_button' && this.history.length === 0) {
      this._renderChips(INITIAL_PILLS.slice(0, this.initialPillCount));
    }
    this.firstExpansion = false;

    if (!(opts && opts.skipFocus)) {
      // small delay lets the layout settle before the keyboard pops on iOS
      var input = this.inputEl;
      setTimeout(function () { try { input.focus({ preventScroll: false }); } catch (_e) { input.focus(); } }, 60);
    }

    // Initial mirror — if filters are already active when the card opens,
    // populate the rail straight away. _renderProducts is a no-op when the
    // grid is empty or no filters are active.
    this._renderProducts();
  };

  BeautyExpertCard.prototype._collapse = function () {
    if (!this.expanded) return;
    this.expanded = false;
    // Return to whichever dormant style the section is configured for.
    var resting = this.dormantMode === 'cta_button' ? 'cta' : 'dormant';
    this.cardEl.setAttribute('data-state', resting);
    this.lastRecommendations = [];
    this._hideRail();
    this._restoreFilterBar();
  };

  // Local-only conversation reset. Wipes the on-screen transcript, drops
  // history (already non-persistent across loads — see readSessionTranscript),
  // and re-renders the opener pills. The bx_sid cookie is preserved so A/B
  // group bucketing stays consistent.
  //
  // Why we DON'T refocus the input afterwards: focusing on a freshly-shrunk
  // panel makes the browser scroll-anchor the page upward, which reads as a
  // jarring "zoom up" jolt. The visitor can tap the input themselves.
  //
  // Why we animate the transcript collapse: clearing innerHTML instantly
  // drops the panel by up to ~280px. That snap is the real source of the
  // zoom-up feeling. We measure the live height, lock it as an explicit
  // value, force layout, then transition max-height + opacity to 0 over
  // ~220ms. CSS cannot animate auto-height directly — the explicit start
  // height is what makes the transition render.
  BeautyExpertCard.prototype._restart = function () {
    var self = this;

    var finalize = function () {
      self.history = [];
      writeSessionTranscript(self.history); // no-op today, keeps the API consistent
      if (self.transcriptEl) {
        self.transcriptEl.innerHTML = '';
        // Hand the transcript back to its CSS-controlled max-height/opacity
        // so the next conversation grows naturally.
        self.transcriptEl.style.maxHeight = '';
        self.transcriptEl.style.opacity = '';
        self.transcriptEl.style.transition = '';
      }
      self.firstExpansion = true;
      if (self.expanded) {
        self._renderChips(INITIAL_PILLS.slice(0, self.initialPillCount));
        self.firstExpansion = false;
      }
      // Clear rail + recommendations on restart. If filters survive the
      // restart (they live in the URL, not the chat history), the next
      // vioriFiltersChanged or v6:cards-rendered event will repopulate
      // the rail with V6 mirror cards (no recommendations until next bot
      // turn).
      self.lastRecommendations = [];
      // Restart returns the card to a greeting state — the next turn the
      // user takes will re-commit the rail to "always shows products".
      self._hasCompletedTurn = false;
      self._hideRail();
    };

    if (this.transcriptEl && this.expanded && this.transcriptEl.children.length > 0) {
      var t = this.transcriptEl;
      var startHeight = t.scrollHeight;
      t.style.maxHeight = startHeight + 'px';
      t.style.opacity = '1';
      // Force reflow so the explicit start values are committed before the
      // next style change — without this the browser collapses both writes
      // into one frame and skips the transition.
      void t.offsetHeight;
      t.style.transition = 'max-height 220ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 180ms ease';
      t.style.maxHeight = '0px';
      t.style.opacity = '0';
      setTimeout(finalize, 240);
    } else {
      finalize();
    }
  };

  BeautyExpertCard.prototype._hideFilterBar = function () {
    var selectors = this.filterBarSelector
      ? [this.filterBarSelector]
      : DEFAULT_FILTER_BAR_SELECTORS;
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) {
        this.filterBarEl = el;
        this.savedFilterBarDisplay = el.style.display;
        el.style.display = 'none';
        return;
      }
    }
  };

  BeautyExpertCard.prototype._restoreFilterBar = function () {
    if (!this.filterBarEl) return;
    this.filterBarEl.style.display = this.savedFilterBarDisplay || '';
    this.filterBarEl = null;
    this.savedFilterBarDisplay = null;
  };

  /* ── Worker warm-up ────────────────────────────────────────── */

  BeautyExpertCard.prototype._warmUp = function () {
    if (this.warmedUp || !this.workerUrl) return;
    this.warmedUp = true;
    try {
      fetch(this.workerUrl + '/api/v1/health', {
        method: 'GET',
        credentials: 'omit',
        cache: 'no-store',
        keepalive: true
      }).catch(function () { /* warm-up is best-effort */ });
    } catch (_e) {
      // older browsers without fetch — bot will still work, just slower first turn
    }
  };

  /* ── Render helpers ────────────────────────────────────────── */

  BeautyExpertCard.prototype._renderChips = function (items) {
    var self = this;
    this.chipsEl.innerHTML = '';
    if (!items || items.length === 0) return;
    items.forEach(function (text) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'be-card__chip';
      // Cart-action chips render with a subtle pink fill so the visitor
      // can tell them apart from chat-prompt chips at a glance.
      if (self._isCartActionLabel(text)) {
        chip.classList.add('be-card__chip--cart-action');
      }
      chip.textContent = text;
      chip.addEventListener('click', function () {
        // "Add to cart" chip is a real action — wire it to /cart/add.js
        // for the top recommended product instead of sending the literal
        // text "Add to cart" to the bot. Bryce 2026-05-03.
        if (self._isCartActionLabel(text)) {
          self._handleCartActionChip(chip, text);
          return;
        }
        self._warmUp();
        self.inputEl.value = text;
        self._expand();
        self._submit();
      });
      self.chipsEl.appendChild(chip);
    });
  };

  /* ── Cart-action chip handling ─────────────────────────────────
   * The bot occasionally returns "Add to cart" (or a close variant) in
   * suggested_followups. Treat those as real cart actions, not chat
   * messages: add the top recommended product to the cart and confirm
   * inline. Conservative match — only labels that read as a direct
   * add-to-cart instruction so we don't intercept questions like
   * "Where is add to cart on the product page?".
   * ────────────────────────────────────────────────────────────── */
  var CART_ACTION_LABELS = [
    'add to cart',
    'add to bag',
    'add it to cart',
    'add this to cart',
    'add to my cart',
    'add it to my cart'
  ];

  BeautyExpertCard.prototype._isCartActionLabel = function (text) {
    if (!text) return false;
    var t = String(text).trim().toLowerCase();
    for (var i = 0; i < CART_ACTION_LABELS.length; i++) {
      if (t === CART_ACTION_LABELS[i]) return true;
    }
    return false;
  };

  // Pick the product whose handle the cart action should target.
  // 1. Top of the bot's last recommendations (rank 1).
  // 2. Fallback: the first card currently rendered in the rail.
  BeautyExpertCard.prototype._pickCartTargetHandle = function () {
    if (this.lastRecommendations && this.lastRecommendations.length > 0) {
      return this.lastRecommendations[0];
    }
    if (this.railTrackEl) {
      var firstCard = this.railTrackEl.querySelector('.v-pcard[data-product-handle]');
      if (firstCard) return firstCard.getAttribute('data-product-handle');
    }
    return null;
  };

  function beCssEscape(value) {
    if (typeof CSS !== 'undefined' && CSS && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  BeautyExpertCard.prototype._handleCartActionChip = function (chipEl, chipText) {
    var self = this;
    var handle = self._pickCartTargetHandle();
    if (!handle) {
      // No recommendation in flight — let the bot know via an inline
      // assistant turn rather than silently no-op.
      self._appendTurn('assistant',
        "I'm not sure which one to add — pick a product from the row below or tell me what you're after."
      );
      return;
    }
    if (chipEl) chipEl.classList.add('is-busy');
    var card = self.railTrackEl
      ? self.railTrackEl.querySelector('.v-pcard[data-product-handle="' + beCssEscape(handle) + '"]')
      : null;
    if (!card) {
      // Rail hasn't rendered the recommended product yet (rare race).
      // Fall back to /products/{handle}.js to fetch the default variant.
      self._addToCartByHandleViaJSON(handle, chipEl);
      return;
    }
    var variantId = card.getAttribute('data-variant-id');
    var variantsCount = parseInt(card.getAttribute('data-variants-count'), 10) || 1;
    var productUrl = card.getAttribute('data-product-url') || '/products/' + handle;
    var productTitle = card.getAttribute('data-product-title') ||
      (card.querySelector('.v-pcard__title') ? card.querySelector('.v-pcard__title').textContent : '');

    // Multi-variant products → send shopper to PDP so they can choose.
    // Matches cust-v6-quick-add.js behaviour exactly.
    if (variantsCount > 1 || !variantId) {
      window.location = productUrl;
      return;
    }
    self._postCartAdd(variantId, productUrl, productTitle, chipEl);
  };

  BeautyExpertCard.prototype._addToCartByHandleViaJSON = function (handle, chipEl) {
    var self = this;
    fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('product fetch ' + r.status); return r.json(); })
      .then(function (product) {
        if (!product || !Array.isArray(product.variants) || product.variants.length === 0) {
          throw new Error('product has no variants');
        }
        if (product.variants.length > 1) {
          // Multi-variant — let the visitor pick on the PDP.
          window.location = (product.url || '/products/' + handle);
          return null;
        }
        var v = product.variants[0];
        if (!v || !v.id) throw new Error('product has no default variant id');
        return self._postCartAdd(v.id, (product.url || '/products/' + handle), (product.title || ''), chipEl);
      })
      .catch(function (err) {
        if (chipEl) chipEl.classList.remove('is-busy');
        console.error('[beauty-expert] cart action JSON fallback failed', err);
        // Final fallback: send the visitor to the PDP so the cart isn't a dead-end.
        window.location = '/products/' + handle;
      });
  };

  BeautyExpertCard.prototype._postCartAdd = function (variantId, productUrl, productTitle, chipEl) {
    var self = this;
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart add ' + res.status);
        return res.json();
      })
      .then(function () {
        return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        var bubbles = document.querySelectorAll('[data-cart-count], [data-js-cart-count]');
        bubbles.forEach(function (b) { b.textContent = cart.item_count; });
        var name = (productTitle || '').trim();
        self._appendTurn('assistant',
          name ? 'Added ' + name + ' to your cart.' : 'Added to your cart.'
        );
        if (chipEl) {
          chipEl.classList.remove('is-busy');
          chipEl.classList.add('is-added');
          chipEl.disabled = true;
        }
      })
      .catch(function (err) {
        if (chipEl) chipEl.classList.remove('is-busy');
        console.error('[beauty-expert] cart add failed', err);
        // Let the visitor finish on the PDP so the chip click isn't a dead-end.
        if (productUrl) window.location = productUrl;
      });
  };

  /**
   * Capture the bot's recommended products from the worker reply.
   *
   * Worker returns:
   *   recommended_products: [{ handle, reason_short, rank }, ...]
   * already validated against post-turn filters. We sort by rank ascending
   * (lower rank = higher priority) and keep just the handle list.
   *
   * If the field is missing, recommendations are cleared — the bot didn't
   * surface anything specific this turn, so the rail falls back to V6 mirror.
   */
  BeautyExpertCard.prototype._captureRecommendations = function (recs) {
    if (!recs || !recs.length) {
      this.lastRecommendations = [];
      return;
    }
    var sorted = recs.slice().sort(function (a, b) {
      var ra = (a && typeof a.rank === 'number') ? a.rank : 9999;
      var rb = (b && typeof b.rank === 'number') ? b.rank : 9999;
      return ra - rb;
    });
    var seen = {};
    var handles = [];
    sorted.forEach(function (r) {
      if (!r || !r.handle) return;
      if (seen[r.handle]) return;
      seen[r.handle] = true;
      handles.push(r.handle);
    });
    this.lastRecommendations = handles;
  };

  /**
   * Render the chat-panel product rail.
   *
   * Order of cards:
   *   1. Bot's recommended products (lastRecommendations, in rank order) —
   *      these come from what the bot just said in chat. Rosemary Shampoo
   *      lands first if the bot just recommended Rosemary Shampoo.
   *   2. Other matching products from the V6 grid (visible cards, in DOM
   *      order, deduped against the recommendations). Filler positions.
   *
   * Total cap of 12 keeps the rail responsive on mobile.
   *
   * Source of card markup: cloned from the V6 grid's already-rendered
   * `.v-pcard` elements. This keeps the carousel pixel-identical to the
   * grid (same image, badges, rating, price, quick-add) without a fetch.
   * Quick-add still works because `cust-v6-quick-add.js` uses document-
   * level delegation on `[data-quick-add]`.
   *
   * Visibility: shown when EITHER recommendations exist OR filters are
   * active. Hidden otherwise.
   */
  BeautyExpertCard.prototype._renderProducts = function () {
    if (!this.railEl || !this.railTrackEl) return;
    if (!this.expanded) { this._hideRail(); return; }

    var hasRecs = this.lastRecommendations && this.lastRecommendations.length > 0;
    var hasFilters = document.body.classList.contains('v-has-filters');

    // Greeting state (no turn yet, no recs, no filters): rail stays hidden.
    // Once any turn has completed, _hasCompletedTurn is true and the rail
    // is committed — Bryce's rule: every input produces a product output
    // below.
    if (!this._hasCompletedTurn && !hasRecs && !hasFilters) {
      this._hideRail();
      return;
    }

    var grid = document.querySelector('[data-v6-grid]');
    if (!grid) {
      // No grid in DOM — only valid path is the greeting state above. If
      // we got here mid-turn, leave whatever's already on the rail alone.
      if (!this._hasCompletedTurn) this._hideRail();
      return;
    }

    // Bump generation counter so any in-flight SRA fetches from the previous
    // render will see a stale gen on resolve and abort.
    this._renderGen++;
    var gen = this._renderGen;
    var self = this;

    // Build a handle → original card lookup. We index ALL cards (including
    // [hidden] ones) so the bot's recommendation can lead even if the V6
    // grid filters happen to hide that product.
    var allCards = grid.querySelectorAll('.v-pcard');
    var byHandle = {};
    for (var i = 0; i < allCards.length; i++) {
      var h = allCards[i].getAttribute('data-product-handle');
      if (h && !byHandle[h]) byHandle[h] = allCards[i];
    }

    // Slot list of length lastRecommendations.length + filler. Each slot is
    // an object: { type: 'card'|'placeholder'|'filler', node, handle }. We
    // build the rail in this slot order so async-fetched cards land at the
    // right rank position when they resolve.
    var slots = [];
    var seen = {};
    var missingHandles = [];

    // 1. Recommendations (rank order). Use cached SRA HTML first; fall back
    // to grid clone only if NOT in a turn-render window; otherwise use
    // placeholder + SRA fetch. This prevents stale grid cards from
    // displacing the bot's actual recommendation during the filter-refetch
    // race (the render-thrash bug).
    var turnEpochAtRender = this._turnEpoch;
    if (hasRecs) {
      for (var j = 0; j < this.lastRecommendations.length; j++) {
        var rh = this.lastRecommendations[j];
        if (!rh || seen[rh]) continue;

        // Prefer cached SRA HTML (from a prior fetch for this handle).
        var cached = this._cardHtmlCache[rh];
        if (cached) {
          var wrapper = document.createElement('div');
          wrapper.innerHTML = cached;
          var cachedCard = wrapper.querySelector('.v-pcard');
          if (cachedCard) {
            cachedCard.removeAttribute('hidden');
            slots.push({ type: 'card', node: cachedCard, handle: rh });
            seen[rh] = true;
            continue;
          }
        }

        // During a turn-render window, skip grid clone (grid may have stale
        // cards from the prior filter state). Use placeholder + SRA instead.
        if (!this._turnRenderActive) {
          var existing = byHandle[rh];
          if (existing) {
            slots.push({ type: 'card', node: cloneCard(existing), handle: rh });
            seen[rh] = true;
            continue;
          }
        }

        // Placeholder + SRA fetch.
        slots.push({ type: 'placeholder', node: buildPlaceholder(j), handle: rh });
        missingHandles.push(rh);
        seen[rh] = true;
      }
    }

    // 2. Fill with V6 grid mirror (visible cards, DOM order, deduped).
    var visible = grid.querySelectorAll('.v-pcard:not([hidden])');
    for (var k = 0; k < visible.length && slots.length < 12; k++) {
      var vh = visible[k].getAttribute('data-product-handle');
      if (vh && seen[vh]) continue;
      slots.push({ type: 'filler', node: cloneCard(visible[k]), handle: vh });
      if (vh) seen[vh] = true;
    }

    if (slots.length === 0) {
      // Two ways to land here:
      //   1. Greeting state (no turn yet) — handled by the early-return above.
      //   2. Mid-turn flux: V6 controller is in the middle of a fetch, the
      //      grid is currently wiped, and the bot didn't recommend anything
      //      we can lean on. Per Bryce's rule, the rail must keep showing
      //      products once a turn has happened — so we leave whatever's on
      //      the rail alone and kick off a Best Sellers SRA backfill that
      //      will paint best-sellers as soon as it resolves.
      if (this._hasCompletedTurn) {
        this._scheduleBestSellersBackfill(gen);
      } else {
        this._hideRail();
      }
      return;
    }

    // Render the slot list.
    var frag = document.createDocumentFragment();
    for (var s = 0; s < slots.length; s++) frag.appendChild(slots[s].node);
    this.railTrackEl.innerHTML = '';
    this.railTrackEl.appendChild(frag);

    this.railEl.classList.remove(
      'be-card__rail--single',
      'be-card__rail--pair',
      'be-card__rail--scroll'
    );
    if (slots.length === 1) this.railEl.classList.add('be-card__rail--single');
    else if (slots.length === 2) this.railEl.classList.add('be-card__rail--pair');
    else this.railEl.classList.add('be-card__rail--scroll');
    this.railEl.hidden = false;

    // Reset scroll so the bot's lead recommendation is in view.
    this.railTrackEl.scrollLeft = 0;

    // 3. SRA fallback: for any recommendation handle not in V6 grid DOM,
    // fetch the V6 card markup and swap it into the placeholder slot.
    // The bot's text references these products so we must show them even
    // if the grid is paginated past them or filtered to a different set.
    if (missingHandles.length > 0) {
      missingHandles.forEach(function (handle) {
        self._fetchCardHtml(handle).then(function (html) {
          // Race guard: a newer render or turn started while we were fetching.
          if (self._renderGen !== gen) return;
          if (self._turnEpoch !== turnEpochAtRender) return;
          if (!html) {
            self._removePlaceholder(handle);
            return;
          }
          self._swapPlaceholder(handle, html);
        }).catch(function () {
          if (self._renderGen !== gen) return;
          if (self._turnEpoch !== turnEpochAtRender) return;
          self._removePlaceholder(handle);
        });
      });
    }

    function cloneCard(orig) {
      var clone = orig.cloneNode(true);
      if (clone.removeAttribute) clone.removeAttribute('id');
      var idEls = clone.querySelectorAll ? clone.querySelectorAll('[id]') : [];
      for (var n = 0; n < idEls.length; n++) idEls[n].removeAttribute('id');
      // If the original was hidden by filters but is a recommendation,
      // ensure the clone is visible.
      clone.removeAttribute('hidden');
      return clone;
    }

    function buildPlaceholder(recIndex) {
      var ph = document.createElement('div');
      ph.className = 'v-pcard be-card__rail-placeholder';
      ph.setAttribute('data-be-placeholder', '1');
      ph.setAttribute('data-be-rec-index', String(recIndex));
      ph.setAttribute('aria-hidden', 'true');
      return ph;
    }
  };

  /**
   * Fetches /collections/all?sort_by=best-selling via SRA and paints the
   * resulting product cards into the rail track. Used as a last-resort
   * backfill when a render would otherwise produce zero slots and the
   * card has already completed a turn — Bryce's rule that every input
   * produces a product output below.
   *
   * Guarded by `_bestSellersInFlight` so concurrent renders don't fan out
   * into multiple fetches. Cached in `_bestSellersHtmlCache` so repeat
   * fallbacks within the same page load are free.
   */
  BeautyExpertCard.prototype._scheduleBestSellersBackfill = function (callerGen) {
    var self = this;
    if (self._bestSellersInFlight) return;

    function paint(html) {
      // Stale-render guard: if the user has interacted and a newer render
      // is in flight, drop this result.
      if (callerGen != null && self._renderGen !== callerGen) return;
      if (!self.expanded || !self.railEl || !self.railTrackEl) return;

      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var cards = doc.querySelectorAll('.v-pcard');
      if (!cards || cards.length === 0) return;

      var frag = document.createDocumentFragment();
      var cap = 12;
      for (var i = 0; i < cards.length && i < cap; i++) {
        var clone = cards[i].cloneNode(true);
        if (clone.removeAttribute) clone.removeAttribute('id');
        var idEls = clone.querySelectorAll ? clone.querySelectorAll('[id]') : [];
        for (var n = 0; n < idEls.length; n++) idEls[n].removeAttribute('id');
        clone.removeAttribute('hidden');
        frag.appendChild(clone);
      }
      // Atomic swap — only replace the existing rail content once we have
      // something to put in. Avoids a "go blank then refill" flicker.
      self.railTrackEl.innerHTML = '';
      self.railTrackEl.appendChild(frag);

      var count = self.railTrackEl.children.length;
      self.railEl.classList.remove(
        'be-card__rail--single',
        'be-card__rail--pair',
        'be-card__rail--scroll'
      );
      if (count === 1) self.railEl.classList.add('be-card__rail--single');
      else if (count === 2) self.railEl.classList.add('be-card__rail--pair');
      else self.railEl.classList.add('be-card__rail--scroll');
      self.railEl.hidden = false;
      self.railTrackEl.scrollLeft = 0;
    }

    if (typeof self._bestSellersHtmlCache === 'string' && self._bestSellersHtmlCache) {
      paint(self._bestSellersHtmlCache);
      return;
    }

    self._bestSellersInFlight = true;
    var url = '/collections/all?sort_by=best-selling' +
              '&sections=main-collection-product-grid';
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        var html = data['main-collection-product-grid'];
        if (typeof html !== 'string' || !html.trim()) return;
        self._bestSellersHtmlCache = html;
        paint(html);
      })
      .catch(function () { /* swallow — best-effort backfill */ })
      .then(function () { self._bestSellersInFlight = false; });
  };

  /**
   * Fetch a V6 product card via Section Rendering API.
   * Returns a Promise resolving to the card HTML string, or null on failure.
   * Cached in-memory by handle (per page load).
   */
  BeautyExpertCard.prototype._fetchCardHtml = function (handle) {
    if (!handle) return Promise.resolve(null);
    if (this._cardHtmlCache[handle] != null) {
      return Promise.resolve(this._cardHtmlCache[handle]);
    }
    var self = this;
    var url = '/products/' + encodeURIComponent(handle) +
              '?sections=cust-v6-card-fragment';
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) return null;
      return r.json();
    }).then(function (data) {
      if (!data) return null;
      var html = data['cust-v6-card-fragment'];
      if (typeof html !== 'string' || !html.trim()) {
        self._cardHtmlCache[handle] = '';
        return null;
      }
      self._cardHtmlCache[handle] = html;
      return html;
    }).catch(function () {
      return null;
    });
  };

  /**
   * Replace the placeholder for `handle` with the fetched card HTML.
   * Strips ids in the parsed result and removes [hidden].
   */
  BeautyExpertCard.prototype._swapPlaceholder = function (handle, html) {
    if (!this.railTrackEl) return;
    // Placeholders carry no data-be-handle attribute — match by
    // data-be-rec-index against lastRecommendations to find the slot for
    // this handle. (The earlier data-be-handle querySelector path was
    // dead code: it referenced an undefined `cssEscape` helper which
    // threw on every call, dropping the swap into the .catch branch in
    // _renderProducts and silently removing the placeholder. Bug found
    // 2026-05-08 via runtime probe.)
    var ph = null;
    var phs = this.railTrackEl.querySelectorAll('[data-be-placeholder]');
    for (var i = 0; i < phs.length; i++) {
      var idx = parseInt(phs[i].getAttribute('data-be-rec-index') || '-1', 10);
      if (idx >= 0 && this.lastRecommendations[idx] === handle) {
        ph = phs[i];
        break;
      }
    }
    if (!ph) return;

    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    var card = wrapper.querySelector('.v-pcard');
    if (!card) {
      ph.parentNode.removeChild(ph);
      return;
    }
    if (card.removeAttribute) card.removeAttribute('id');
    var idEls = card.querySelectorAll ? card.querySelectorAll('[id]') : [];
    for (var n = 0; n < idEls.length; n++) idEls[n].removeAttribute('id');
    card.removeAttribute('hidden');
    ph.parentNode.replaceChild(card, ph);
  };

  /**
   * Remove the placeholder for `handle` (used when SRA fetch returns nothing).
   * Re-applies the rail size class since slot count changed.
   */
  BeautyExpertCard.prototype._removePlaceholder = function (handle) {
    if (!this.railTrackEl || !this.railEl) return;
    var phs = this.railTrackEl.querySelectorAll('[data-be-placeholder]');
    for (var i = 0; i < phs.length; i++) {
      var idx = parseInt(phs[i].getAttribute('data-be-rec-index') || '-1', 10);
      if (idx >= 0 && this.lastRecommendations[idx] === handle) {
        phs[i].parentNode.removeChild(phs[i]);
        break;
      }
    }
    var count = this.railTrackEl.children.length;
    this.railEl.classList.remove(
      'be-card__rail--single',
      'be-card__rail--pair',
      'be-card__rail--scroll'
    );
    if (count === 0) {
      // Per Bryce's rule — never collapse the rail to empty after a turn
      // has completed. Trigger a Best Sellers backfill instead.
      if (this._hasCompletedTurn) {
        this._scheduleBestSellersBackfill(null);
      } else {
        this._hideRail();
      }
      return;
    }
    if (count === 1) this.railEl.classList.add('be-card__rail--single');
    else if (count === 2) this.railEl.classList.add('be-card__rail--pair');
    else this.railEl.classList.add('be-card__rail--scroll');
  };

  BeautyExpertCard.prototype._hideRail = function () {
    if (!this.railEl) return;
    this.railEl.hidden = true;
    if (this.railTrackEl) this.railTrackEl.innerHTML = '';
    this.railEl.classList.remove(
      'be-card__rail--single',
      'be-card__rail--pair',
      'be-card__rail--scroll'
    );
  };

  BeautyExpertCard.prototype._renderHistory = function () {
    var self = this;
    this.transcriptEl.innerHTML = '';
    this.history.forEach(function (msg) {
      if (msg.role === 'user') {
        self._appendTurn('user', msg.text, { instant: true });
      } else if (msg.role === 'assistant') {
        self._appendTurn('assistant', msg.text, { instant: true });
      }
    });
    this._scrollToEnd();
  };

  BeautyExpertCard.prototype._appendTurn = function (role, text, opts) {
    opts = opts || {};
    var row = document.createElement('div');
    row.className = 'be-turn be-turn--' + role;
    var avatarSvg = role === 'assistant' ? this._renderBotAvatar() : this._renderUserAvatarSvg();
    var label = role === 'assistant' ? this.botName : YOU_LABEL;
    row.innerHTML =
      '<div class="be-turn__avatar" aria-hidden="true">' + avatarSvg + '</div>' +
      '<div class="be-turn__body">' +
        '<div class="be-turn__label">' + escapeHtml(label) + '</div>' +
        '<div class="be-turn__text"></div>' +
      '</div>';
    var textEl = row.querySelector('.be-turn__text');

    if (opts.instant) {
      textEl.textContent = text;
    } else if (role === 'assistant' && text) {
      this._typewriter(textEl, text);
    } else {
      textEl.textContent = text;
    }

    this.transcriptEl.appendChild(row);
    this._scrollToEnd();
    return row;
  };

  BeautyExpertCard.prototype._appendTyping = function () {
    var row = document.createElement('div');
    row.className = 'be-turn be-turn--assistant be-turn--typing';
    row.innerHTML =
      '<div class="be-turn__avatar" aria-hidden="true">' + this._renderBotAvatar() + '</div>' +
      '<div class="be-turn__body">' +
        '<div class="be-turn__label">' + escapeHtml(this.botName) + '</div>' +
        '<div class="be-turn__typing"><i></i><i></i><i></i></div>' +
      '</div>';
    this.transcriptEl.appendChild(row);
    this._scrollToEnd();
    return row;
  };

  BeautyExpertCard.prototype._typewriter = function (el, text) {
    var self = this;
    var i = 0;
    var len = text.length;
    el.textContent = '';
    function tick() {
      if (i >= len) return;
      // Append a small batch each tick — typewriter feel without per-char overhead.
      var batch = Math.min(3, len - i);
      el.textContent += text.substr(i, batch);
      i += batch;
      self._scrollToEnd();
      setTimeout(tick, TYPEWRITER_MS_PER_CHAR);
    }
    tick();
  };

  BeautyExpertCard.prototype._appendEscalation = function (esc) {
    if (!esc) return;
    var row = document.createElement('div');
    row.className = 'be-escalation';
    var instruction = (esc.instruction_to_user || '').trim();
    var html = '<div class="be-escalation__msg">' + escapeHtml(instruction) + '</div>' +
      '<div class="be-escalation__actions">' +
        '<button type="button" class="be-escalation__btn be-escalation__btn--redo">Chat with a person</button>' +
        '<a class="be-escalation__btn be-escalation__btn--mailto" ' +
          'href="mailto:support@viori.com?subject=Beauty%20Expert%20question">Email us</a>' +
      '</div>';
    row.innerHTML = html;

    var redoBtn = row.querySelector('.be-escalation__btn--redo');
    redoBtn.addEventListener('click', function () {
      // Re:do widget integration TBD per Phase A — try a couple of well-known
      // global hooks; fall back to a contact URL.
      if (window.ReeDo && typeof window.ReeDo.openChat === 'function') {
        window.ReeDo.openChat();
        return;
      }
      if (window.Redo && typeof window.Redo.openChat === 'function') {
        window.Redo.openChat();
        return;
      }
      window.open('https://viori.com/pages/contact', '_blank', 'noopener');
    });

    this.transcriptEl.appendChild(row);
    this._scrollToEnd();
  };

  BeautyExpertCard.prototype._scrollToEnd = function () {
    if (!this.transcriptEl) return;
    this.transcriptEl.scrollTop = this.transcriptEl.scrollHeight;
  };

  /* ── Submit + worker ───────────────────────────────────────── */

  BeautyExpertCard.prototype._submit = function () {
    if (this.isSending) return;
    var raw = this.inputEl.value;
    var text = (raw || '').trim();
    if (text.length === 0) return;
    if (text.length > TURN_INPUT_MAXLEN) text = text.slice(0, TURN_INPUT_MAXLEN);

    this.inputEl.value = '';
    this.isSending = true;
    this.sendBtnEl.disabled = true;

    var nowIso = new Date().toISOString();
    var userMsg = { role: 'user', text: text, ts: nowIso };

    this._appendTurn('user', text, { instant: true });
    var typingRow = this._appendTyping();

    var self = this;
    this._postTurn(userMsg)
      .then(function (resp) {
        self.history.push(userMsg);
        var replyText = (resp && resp.reply && resp.reply.text) ? resp.reply.text : '';
        if (replyText) {
          self.history.push({
            role: 'assistant',
            text: replyText,
            ts: new Date().toISOString()
          });
        }
        if (self.history.length > HISTORY_LIMIT) {
          self.history = self.history.slice(-HISTORY_LIMIT);
        }
        writeSessionTranscript(self.history);

        typingRow.remove();
        self._appendTurn('assistant', replyText, { instant: false });

        // Render LLM-generated follow-up chips. Always replace the chip row
        // with whatever the bot returned this turn — including the empty
        // case. Leaving the previous turn's chips up (or worse, the openers)
        // creates a stale-UI bug: the visitor sees pills that don't match
        // the conversation. The worker prompt (be.v1.8+) is responsible for
        // emitting non-empty followups on every non-escalation turn.
        var chips = (resp && resp.reply && Array.isArray(resp.reply.suggested_followups))
          ? resp.reply.suggested_followups
          : [];
        self._renderChips(chips);

        // Capture the bot's recommended products for this turn. The worker
        // returns them as `[{ handle, reason_short, rank }, ...]`, validated
        // for catalog membership only (filter mismatch is logged but not
        // dropped — the carousel still leads with the bot's text).
        // be-client-2.2.0: suppress event-driven rail re-renders during
        // the turn response window so the grid's SRA refetch doesn't
        // thrash the rail with stale/intermediate DOM state.
        self._turnRenderActive = true;
        self._turnEpoch++;

        self._captureRecommendations(resp && resp.recommended_products);
        // Commit the rail to "always shows products" state. Once true,
        // _renderProducts will never collapse the rail to empty mid-fetch.
        self._hasCompletedTurn = true;

        // Diagnostic — surfaces in DevTools so we can verify the chain at a
        // glance. Stash the full response on window for ad-hoc inspection.
        try {
          window.__beautyExpertLastResponse = resp;
          var recs = (resp && resp.recommended_products) || [];
          console.log(
            '[BE] turn complete — recommended_products:', recs.length,
            'handles:', recs.map(function (r) { return r.handle; })
          );
        } catch (_e) { /* ignore */ }

        self._dispatchDirectives(resp && resp.directives);
        self._appendEscalation(resp && resp.escalation);

        // Refresh the rail now that recommendations + filter directives have
        // landed. Event-driven re-renders from vioriFiltersChanged and
        // v6:cards-rendered are suppressed by _turnRenderActive — only this
        // explicit call renders during the turn window.
        self._renderProducts();

        // Release the render lock after a timeout so event-driven renders
        // resume once the grid's SRA fetch has had time to settle.
        setTimeout(function () { self._turnRenderActive = false; },
                   self._turnRenderTimeoutMs);
      })
      .catch(function (err) {
        typingRow.remove();
        var msg = "I'm having trouble reaching the assistant. Try again in a moment, or use the filters above.";
        if (err && err.code === 'rate_limit_exceeded') {
          msg = "We're seeing heavy traffic — give us a moment and try again.";
        } else if (err && err.code === 'model_unavailable') {
          msg = "We're briefly unavailable — try that again in a moment.";
        }
        self._appendTurn('assistant', msg, { instant: true });
      })
      .then(function () {
        self.isSending = false;
        self.sendBtnEl.disabled = false;
        if (self.expanded) self.inputEl.focus();
      });
  };

  BeautyExpertCard.prototype._dispatchDirectives = function (directives) {
    if (!directives) return;
    var hasApply = directives.filters_to_apply &&
      Object.keys(directives.filters_to_apply).length > 0;
    var hasClear = directives.filters_to_clear && directives.filters_to_clear.length > 0;
    if (!hasApply && !hasClear) return;

    // V6 production exposes window.VioriFilters (plural). Older builds used
    // window.VioriFilter (singular). Try both so this bundle works against
    // either controller without a coordinated swap.
    var fc = window.VioriFilters || window.VioriFilter;
    if (!fc || typeof fc.applyFilters !== 'function') {
      console.warn('[BeautyExpert] No filter controller found on window. ' +
        'Filter directives ignored:', directives);
      return;
    }
    fc.applyFilters({
      source: 'bot',
      filters: directives.filters_to_apply || {},
      clear: directives.filters_to_clear || [],
      merge: true
    });
  };

  BeautyExpertCard.prototype._postTurn = function (userMessage) {
    if (!this.workerUrl) {
      return Promise.reject({ code: 'config_error', message: 'No worker URL configured.' });
    }

    var body = {
      session_id: this.sessionId,
      turn_id: uuidv4(),
      message: userMessage,
      conversation: { history: this.history.slice(-HISTORY_LIMIT) },
      page_context: {
        url: location.href,
        template: 'collection',
        collection_handle: getCollectionHandle(),
        current_filters: getCurrentFilters()
      },
      client: {
        viewport: getViewport(),
        locale: this.locale,
        bundle_version: BUNDLE_VERSION
      }
    };

    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timeout = setTimeout(function () { if (controller) controller.abort(); }, TURN_TIMEOUT_MS);

    return fetch(this.workerUrl + '/api/v1/turn', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        clearTimeout(timeout);
        return res.json().then(function (json) {
          if (!res.ok) {
            var err = (json && json.error) || { code: 'http_' + res.status, message: 'Request failed' };
            throw err;
          }
          return json;
        });
      })
      .catch(function (err) {
        clearTimeout(timeout);
        throw err;
      });
  };

  /* ── Auto-mount with A/B + path gating ─────────────────────── */

  function autoMount() {
    var el = document.querySelector('[data-beauty-expert]');
    if (!el || el.dataset.beautyExpertReady === '1') return;

    // Path gate — only mount on /collections/all
    if (!ALLOWED_PATH_PATTERN.test(location.pathname)) {
      el.dataset.beautyExpertSkipped = 'path';
      return;
    }

    // A/B group gate — control group sees nothing.
    var sid = getOrCreateSessionId();
    var bucket = abGroupBucket(sid);
    if (bucket >= AB_TEST_THRESHOLD) {
      el.dataset.beautyExpertSkipped = 'control';
      return;
    }

    el.dataset.beautyExpertReady = '1';
    el.removeAttribute('hidden');
    window.VioriBeautyExpert = new BeautyExpertCard(el);
  }

  // Theme-editor hot reload handler. When Shopify reloads a section (the
  // merchant tweaks a setting), the section's DOM is replaced. Our previous
  // instance still holds DOM refs to detached nodes, and — worse — the V6
  // Filter Bar may have been left with inline display:none if the merchant
  // saves while the chat was expanded. Defensively undo the filter-bar hide
  // and drop the previous instance before remounting fresh.
  function teardownPrevious() {
    try {
      var prev = window.VioriBeautyExpert;
      if (prev && typeof prev._restoreFilterBar === 'function') {
        prev._restoreFilterBar();
      }
    } catch (_e) { /* ignore */ }

    // Belt-and-braces: walk the default filter-bar selectors and clear any
    // stuck inline display:none that we might have left behind. Only clears
    // the inline style; CSS-driven display rules are untouched.
    for (var i = 0; i < DEFAULT_FILTER_BAR_SELECTORS.length; i++) {
      var fb = document.querySelector(DEFAULT_FILTER_BAR_SELECTORS[i]);
      if (fb && fb.style.display === 'none') {
        fb.style.display = '';
      }
    }

    window.VioriBeautyExpert = null;
  }

  function handleSectionLoad(event) {
    // shopify:section:load fires for every section reload. We only want to
    // re-mount when our section is the one that reloaded. Detect via the
    // event target containing our mount element, OR fall back to a generic
    // re-mount if the target lookup is ambiguous (cheap, idempotent).
    var target = event && event.target;
    var ourSection = false;
    if (target && target.querySelector) {
      ourSection = !!target.querySelector('[data-beauty-expert]');
    }
    if (!ourSection) {
      // Could be an unrelated section reload; still safe to no-op here.
      // But we double-check: if our mount exists in the page but is missing
      // the ready flag (i.e., section was replaced and our previous instance
      // is now stale), treat that as ours.
      var existing = document.querySelector('[data-beauty-expert]');
      if (!existing) return;
      if (existing.dataset.beautyExpertReady === '1') return;
      // Fall through to teardown + mount.
    }
    teardownPrevious();
    autoMount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
  document.addEventListener('shopify:section:load', handleSectionLoad);
  // Theme editor also fires shopify:section:unload when the section is
  // removed — restore the filter bar so we don't leave it hidden.
  document.addEventListener('shopify:section:unload', function (event) {
    var target = event && event.target;
    if (target && target.querySelector && target.querySelector('[data-beauty-expert]')) {
      teardownPrevious();
    }
  });

})();
